import { useEffect } from "react";
import { SITE_URL, cargarDatos } from "./datos";

const ORG_ID = `${SITE_URL}/#organizacion`;
const limpiar = (o) => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined && v !== null && v !== ""));
export const absoluta = (u) => (!u ? "" : /^https?:\/\//.test(u) ? u : `${SITE_URL}${u.startsWith("/") ? "" : "/"}${u}`);
const url = (ruta) => (ruta === "/" ? `${SITE_URL}/` : `${SITE_URL}${ruta}`);

// Datos públicos de NuvliPro (sin calle). Los rellenados en Administración SEO mandan sobre estos.
const ORG = { email: "info@nuvlipro.com", telefono: "+34 686 64 57 80", codigo_postal: "38670", localidad: "Adeje", provincia: "Santa Cruz de Tenerife" };

export function organizacion(d) {
  const o = { ...ORG, ...limpiar(d?.ajustes?.organizacion || {}) };
  const direccion = limpiar({ "@type": "PostalAddress", postalCode: o.codigo_postal, addressLocality: o.localidad,
    addressRegion: o.provincia, addressCountry: "ES" });
  return limpiar({ "@type": "Organization", "@id": ORG_ID, name: o.nombre || "NuvliPro", url: SITE_URL,
    logo: absoluta("/nuvlipro-logo.png"), email: o.email, telephone: o.telefono, address: direccion,
    sameAs: o.redes?.length ? o.redes : undefined });
}

export const sitioWeb = () => ({ "@type": "WebSite", "@id": `${SITE_URL}/#web`, name: "NuvliPro", url: url("/"), inLanguage: "es-ES", publisher: { "@id": ORG_ID } });

// Mismos planes y precios que la tabla de precios (vienen de PLANS en el servidor); sin valoraciones.
const DURACION = { 1: "P1M", 3: "P3M", 12: "P1Y" };
const oferta = (p, d) => {
  const precio = Number(p.precio).toFixed(2);
  const duracion = DURACION[d?.planes_detalle?.[p.id]?.meses];
  return limpiar({ "@type": "Offer", name: p.nombre, price: precio, priceCurrency: "EUR", description: p.periodo, url: `${url("/")}#precios`,
    priceSpecification: duracion ? { "@type": "UnitPriceSpecification", price: precio, priceCurrency: "EUR", billingDuration: duracion } : undefined });
};

export const software = (d) => ({
  "@type": "SoftwareApplication", name: "NuvliPro", applicationCategory: "BusinessApplication", operatingSystem: "Web, Android, iOS",
  url: url("/"), inLanguage: "es-ES", publisher: { "@id": ORG_ID },
  offers: (d?.planes || []).map((p) => oferta(p, d)),
});

export const migas = (items) => ({
  "@type": "BreadcrumbList",
  itemListElement: [["Inicio", "/"], ...items].map(([name, ruta], i) => ({ "@type": "ListItem", position: i + 1, name, item: url(ruta) })),
});

// Las preguntas se muestran como texto: sin los «##» de los títulos del editor.
export const sinAlmohadillas = (s) => String(s || "").replace(/^\s*#+\s*/gm, "").trim();

export const faqPage = (faqs) => ({
  "@type": "FAQPage",
  mainEntity: (faqs || []).map((f) => ({ "@type": "Question", name: sinAlmohadillas(f.pregunta),
    acceptedAnswer: { "@type": "Answer", text: sinAlmohadillas(f.respuesta) } })),
});

export const articuloLd = (a, ruta) => limpiar({
  "@type": "Article", headline: a.titulo, description: a.descripcion_seo || a.resumen, image: a.imagen ? [absoluta(a.imagen)] : undefined,
  datePublished: a.fecha, dateModified: (a.updated_at || a.fecha || "").slice(0, 10), inLanguage: "es-ES",
  author: !a.autor || /^nuvlipro$/i.test(a.autor) ? { "@type": "Organization", name: "NuvliPro", url: url("/") } : { "@type": "Person", name: a.autor },
  publisher: { "@type": "Organization", "@id": ORG_ID, name: "NuvliPro" }, mainEntityOfPage: { "@type": "WebPage", "@id": url(ruta) },
});

export function meta({ ruta, titulo, descripcion, imagen, tipo = "website", d, jsonld = [], robots }) {
  return {
    title: titulo || "NuvliPro", description: descripcion || "", canonical: url(ruta), type: tipo, robots,
    image: absoluta(imagen || d?.ajustes?.imagen_og || "/og-nuvlipro.jpg"),
    jsonld: { "@context": "https://schema.org", "@graph": [organizacion(d), ...jsonld] },
  };
}

const og = (m) => [["og:type", m.type], ["og:site_name", "NuvliPro"], ["og:locale", "es_ES"], ["og:title", m.title],
  ["og:description", m.description], ["og:url", m.canonical], ["og:image", m.image]];
const tw = (m) => [["twitter:card", "summary_large_image"], ["twitter:title", m.title], ["twitter:description", m.description], ["twitter:image", m.image]];

function poner(selector, crear, attr, valor) {
  let el = document.head.querySelector(selector);
  if (!valor) { el?.remove(); return; }
  if (!el) {
    el = document.createElement(crear.tag);
    Object.entries(crear.attrs).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  el.setAttribute(attr, valor);
}

export function aplicarCabecera(m) {
  document.title = m.title;
  poner('meta[name="description"]', { tag: "meta", attrs: { name: "description" } }, "content", m.description);
  poner('link[rel="canonical"]', { tag: "link", attrs: { rel: "canonical" } }, "href", m.canonical);
  // Propia de la cabecera (data-cabecera): no toca la de useNoindexSiVacia.
  poner('meta[name="robots"][data-cabecera]', { tag: "meta", attrs: { name: "robots", "data-cabecera": "" } }, "content", m.robots);
  og(m).forEach(([k, v]) => poner(`meta[property="${k}"]`, { tag: "meta", attrs: { property: k } }, "content", v));
  tw(m).forEach(([k, v]) => poner(`meta[name="${k}"]`, { tag: "meta", attrs: { name: k } }, "content", v));
  let s = document.getElementById("seo-jsonld");
  if (!s) {
    s = document.createElement("script");
    s.type = "application/ld+json";
    s.id = "seo-jsonld";
    document.head.appendChild(s);
  }
  s.textContent = JSON.stringify(m.jsonld);
}

export function useCabecera(m) {
  const clave = m ? JSON.stringify(m) : "";
  useEffect(() => { if (clave) aplicarCabecera(JSON.parse(clave)); }, [clave]);
}

// /blog y /opiniones vacías: noindex solo en el navegador y con los datos de la API (nunca en el HTML pregenerado),
// así desaparece sola al publicar el primer artículo o la primera opinión.
export function useNoindexSiVacia(clave) {
  useEffect(() => {
    if (!clave) return undefined;
    let vivo = true;
    let m = null;
    cargarDatos().then((d) => {
      if (!vivo || d.respaldo || d[clave]?.length) return;
      m = document.createElement("meta");
      m.name = "robots";
      m.content = "noindex";
      document.head.appendChild(m);
    }).catch(() => {});
    return () => { vivo = false; m?.remove(); };
  }, [clave]);
}

// Páginas que no existen: sin canonical (la genérica del HTML apuntaría a la portada).
export function useSinCanonica() {
  useEffect(() => { document.head.querySelector('link[rel="canonical"]')?.remove(); }, []);
}

const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// Etiquetas de <head> para el HTML prerenderizado.
export function cabeceraHtml(m) {
  return [
    `<meta name="description" content="${esc(m.description)}">`,
    `<link rel="canonical" href="${esc(m.canonical)}">`,
    ...(m.robots ? [`<meta name="robots" content="${esc(m.robots)}" data-cabecera="">`] : []),
    ...og(m).map(([k, v]) => `<meta property="${k}" content="${esc(v)}">`),
    ...tw(m).map(([k, v]) => `<meta name="${k}" content="${esc(v)}">`),
    `<script type="application/ld+json" id="seo-jsonld">${JSON.stringify(m.jsonld).replace(/</g, "\\u003c")}</script>`,
  ].join("");
}
